'use strict';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import has from 'lodash/has';
import isFunction from 'lodash/isFunction';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import {SortMethod, SyncStatus, ViewMode} from './enums';
import {listToArray} from './utils';

const hasOwnProperty = Object.prototype.hasOwnProperty;

/**
 * @typedef {Object} ReduxObservableListReducers
 * @property {Object} initialState
 * @property {Function} reducer
 * @property {Function} listReducer
 * @property {Function} loadSuccessReducer
 * @property {Function} updateVisibility
 * @property {Function} updatesReducer Use to just update the updates list
 * @property {Function} updateSyncStatusUpdatesReducer Use to update the updates list and calculate the new sync status
 * @property {Function} hasValidParentId
 */

/**
 * @typedef {Object} ReduxObservableListState
 * @property {Boolean} init
 * @property {ViewMode} mode
 * @property {Boolean} filteredHasMore
 * @property {Boolean} hasMore
 * @property {Boolean} isFiltered
 * @property {Number} currentPage
 * @property {Number} currentFilteredPage
 * @property {Number} total
 * @property {Number} filteredTotal
 * @property {Object<String, T>} list
 * @property {Array<Array<T>>} filteredPages Caches a copy of the current filter's page results from the server, as is
 * @property {Array<Array<T>>} pages Caches a copy of the page results from the server, as is
 * @property {?Error} err The latest error from attempting to fetch list data
 * @property {String} [parentId] Exists when hasParentId==true.
 * @property {String} [filter] Exists when useAutoFilter==true.
 * @property {Object} [filterOptions] Exists when useFilterOptions==true.
 * @property {ReduxListSortOptions} [sortOptions] Exists when useSortOptions==true.
 * @property {Object<String, T>} deleting Exists when useDelete==true.
 * @property {Object<String, ReduxObservableListState~update>} updates Exists when useSyncUpdates==true.
 */

/**
 * @typedef {Object} ReduxObservableListState~update
 * @property {SyncStatus} status
 */

/**
 * @typedef {Object} ReduxListSortOptions
 * @typedef {SortMethod} currentMethod
 * @typedef {String} property
 */

/**
 * TODO: add property for matching filterOptions
 * TODO: add state accessors for stuff like the filter data and list
 * @param {ReduxObservableListActions} actions
 * @param {ReduxObservableListOptions} options
 * @return {ReduxObservableListReducers}
 */
export function buildListReducer(actions, options) {
  /** @type {ReduxObservableListState} */
  const initialState = Object.assign({
    init: false,
    mode: ViewMode.LOADING,
    isLoadingPage: false,
    filteredHasMore: false,
    hasMore: true,
    isFiltered: false,
    currentPage: 0,
    currentFilteredPage: 0,
    total: 0,
    filteredTotal: 0,
    list: {},
    err: null,
  }, options.initialState || {});

  if (options.hasParentId)
    initialState.parentId = null;

  if (options.useAutoFilter)
    initialState.filter = '';

  if (options.useFilterOptions)
    initialState.filterOptions = cloneDeep(options.defaultFilterOptions);

  if (options.useSortOptions)
    initialState.sortOptions = Object.assign({
      currentMethod: SortMethod.ASCENDING,
      property: 'name',
    }, options.defaultSortOptions);

  if (options.useDelete)
  // TODO: implement using 'deleting' to store which ids are currently being deleted so sync can handle them properly
  // TODO: alternatively, use 'updating'. potentially adding the optimistic updates system in here directly anyway.
    initialState.deleting = {};

  if (options.useSyncUpdates)
    initialState.updates = {};

  const getListId = options.useSyncUpdates && options.canSyncCreate ? getListIdCanCreate : getListIdNotCanCreate;

  return {
    initialState,
    reducer,
    listReducer,
    loadSuccessReducer,
    updateVisibility,
    getListId,
    updatesReducer,
    updateSyncStatusUpdatesReducer,
    hasValidParentId,
  };

  /**
   * @param {ReduxObservableListState} state
   * @param {ReduxAction} action
   * @returns {ReduxObservableListState}
   */
  function reducer(state = initialState, action) {
    /** @type {ReduxObservableListState} */
    let nextState;
    switch (action.type) {
      case actions.hydrateList.TYPE:
        return hasValidParentId(state, action) ? action.payload : state;
      case actions.initList.TYPE:
        return initListReducer(state, action);
      case actions.syncListSuccess.TYPE:
        return syncListSuccessReducer(state, action);
      case actions.loadSuccess.TYPE:
        return loadSuccessReducer(state, action);
      case actions.loadPageError.TYPE:
        if (!hasValidParentId(state, action))
          return state;

        return {
          ...state,
          mode: action.payload.mode,
          err: action.payload.err,
          isLoadingPage: false,
        };
      case actions.loadPage.TYPE:
        if (!hasValidParentId(state, action))
          return state;

        return {
          ...state,
          mode: Object.keys(state.list).length === 0 ? ViewMode.LOADING : ViewMode.LOADING_MORE,
          isLoadingPage: true,
        };
      case actions.loadPageSuccess.TYPE:
        if (!hasValidParentId(state, action))
          return state;

        nextState = {
          ...state,
          mode: ViewMode.SUCCESS,
          err: null,
          isLoadingPage: false,
        };

        if (!nextState.isFiltered) {
          nextState.hasMore = action.payload.hasMore;
          nextState.currentPage = action.payload.currentPage + 1;
          nextState.total = action.payload.total;
        } else {
          nextState.filteredHasMore = action.payload.hasMore;
          nextState.currentFilteredPage = action.payload.currentPage + 1;
          nextState.filteredTotal = action.payload.total;
        }

        return nextState;
      case actions.applyFilter.TYPE:
        if (!hasValidParentId(state, action))
          return state;

        nextState = applyFilterReducer(state, action);

        // TODO(redux): why is this here? what is this fixing?
        if (action.payload.filter)
          nextState.mode = ViewMode.SUCCESS;

        return nextState;
      case actions.destroyList.TYPE:
        return hasValidParentId(state, action) ? initialState : state;

      case actions.deleteItem.TYPE:
      case actions.deleteItemSuccess.TYPE:
      case actions.deleteItemError.TYPE:
        if (!hasValidParentId(state, action))
          return state;

        // do nothing if state IS the initialState because we definitely do not have a channel to update
        return state === initialState ? initialState : {
          ...state,
          list: listReducer(state.list, action, state),
        };

      case actions.updateSyncStatus.TYPE:
        return updateSyncStatusReducer(state, action);

      default:
        return state;
    }
  }

  /**
   * @param {ReduxObservableListState} state
   * @param {ReduxAction<InitListPayload>} action
   * @returns {ReduxObservableListState}
   */
  function initListReducer(state, action) {
    /** @type {InitListPayload} */
    const payload = action.payload;
    let nextState = {
      ...state,
    };

    if (options.hasParentId) {
      // force cleanup if another list was loaded, then report as an error
      if (state.init && state.parentId !== payload.parentId) {
        nextState = {
          ...initialState,
        };
        options.logger.error(`redux-observable-list reducer initList called with new parentId without destroyList first.`);
      }

      nextState.parentId = payload.parentId;
    }

    if (!state.init) {
      nextState.init = true;
      nextState.mode = ViewMode.LOADING;
      nextState.err = null;
    }

    // This is intentionally nextState, not state.
    // We need to apply the filter logic to the newly initialized state, not whatever came before.
    const filterData = getNextFilterData(nextState, action);
    if (filterData.filter || filterData.filterOptions) {
      nextState = applyFilterReducer(nextState, action);
    }

    return nextState;
  }

  function applyFilterReducer(state, action) {
    const filterData = getNextFilterData(state, action);
    const nextState = {
      ...state,
      ...filterData,
      list: listReducer(state.list, action, filterData),
    };

    nextState.isFiltered = !options.isFilterDataDefault(filterData);

    // only change if we are changing isFiltered
    if (!state.isFiltered && nextState.isFiltered) {
      nextState.currentFilteredPage = 0;
      nextState.filteredHasMore = state.hasMore;
    }

    if (!nextState.filteredHasMore) {
      nextState.filteredTotal = nextState.total === 0 ? 0 : listToArray(options, nextState).length;
    }

    return nextState;
  }

  /**
   * @param state
   * @param action
   * @return {FilterData}
   */
  function getNextFilterData(state, action) {
    const filterData = {};

    if (options.useAutoFilter)
      filterData.filter = has(action.payload, 'filter') ? action.payload.filter : state.filter;
    if (options.useFilterOptions)
      filterData.filterOptions = has(action.payload, 'filterOptions') ? action.payload.filterOptions : state.filterOptions;
    if (options.useSortOptions)
      filterData.sortOptions = has(action.payload, 'sortOptions') ? action.payload.sortOptions : state.sortOptions;

    return filterData;
  }

  /**
   * @param {ReduxObservableListState} state
   * @param {ReduxAction<*>} action
   */
  function hasValidParentId(state, action) {
    if (options.hasParentId) {
      const parentId = action.meta && action.meta.parentId;
      return parentId === state.parentId;
    } else {
      return true;
    }
  }

  /**
   * @param {ReduxObservableListState} state
   * @param {ReduxAction<SyncListSuccessPayload>} action
   */
  function syncListSuccessReducer(state, action) {
    if (!hasValidParentId(state, action) || action.payload.isFiltered !== state.isFiltered)
      return state;

    const nextState = loadSuccessReducer(state, action);

    if (!nextState.isFiltered) {
      if (nextState.currentPage === action.payload.currentPage) {
        nextState.hasMore = action.payload.hasMore;
        nextState.total = action.payload.total;
      }
    } else {
      if (nextState.currentFilteredPage === action.payload.currentPage) {
        nextState.filteredHasMore = action.payload.hasMore;
        nextState.filteredTotal = action.payload.total;
      }
    }

    if (state.mode === ViewMode.LOADING) {
      nextState.mode = ViewMode.SUCCESS;
    }

    return nextState;
  }

  function listReducer(state = initialState.list, action, filterData) {
    switch (action.type) {
      case actions.applyFilter.TYPE:
        return updateVisibility(state, filterData);

      case actions.deleteItem.TYPE:
      case actions.deleteItemSuccess.TYPE:
      case actions.deleteItemError.TYPE:
        const itemId = action.payload.id;
        if (has(state, itemId)) {
          return deleteItemReducer(state, action, itemId);
        } else {
          return state;
        }

      default:
        return state;
    }
  }

  /**
   * @param state
   * @param {ReduxAction<LoadSuccessPayload>} action
   */
  function loadSuccessReducer(state, action) {
    if (!hasValidParentId(state, action))
      return state;

    const nextState = {
      ...state,
      err: null,
    };

    const list = Object.assign({}, state.list);
    const listUpdate = Object(action.payload.list);

    for (let key in listUpdate) {
      if (hasOwnProperty.call(listUpdate, key)) {
        const nextItem = loadSuccessItemProcessingReducer(listUpdate[key], list[key], state);
        list[key] = loadSuccessItemFilteredReducer(nextItem, list[key], state);
      }
    }

    // clear nulls
    nextState.list = pickBy(list);

    return nextState;
  }

  function loadSuccessItemProcessingReducer(nextItem, item, state) {
    if (item && isProcessing(item, state)) {
      return handleProcessingConflict(item, nextItem, state)
    } else {
      return nextItem;
    }
  }

  function loadSuccessItemFilteredReducer(nextItem, item, state) {
    if (nextItem && state.isFiltered) {
      return {
        ...nextItem,
        isVisible: options.isVisible(nextItem, state),
      };
    } else {
      return nextItem;
    }
  }

  /**
   * User may override if they are using optimistic updates or some other logic.
   * Conflicts in isProcessing items may be handled with custom logic.
   */
  function isProcessing(item, state) {
    if (options.isProcessing && options.isProcessing(item, options, state)) {
      return true;
    } else if (!options.useDelete) {
      return false;
    } else {
      return item.isDeleting;
    }
  }

  function handleProcessingConflict(oldItem, newItem, state) {
    if (options.handleProcessingConflict) {
      return options.handleProcessingConflict(oldItem, newItem, options, state);
    } else if (newItem === null) {
      return null;
    } else if (options.useDelete && has(oldItem, 'isDeleting')) {
      return Object.assign({}, oldItem, newItem, {isDeleting: oldItem.isDeleting});
    } else {
      return Object.assign({}, oldItem, newItem);
    }
  }

  /**
   * Called when we've already determined that state contains the current item ID
   * @param {Object<String, Object>} state
   * @param {ReduxAction} action
   * @param {String} itemId
   * @return {*}
   */
  function deleteItemReducer(state, action, itemId) {
    const channel = state[itemId];
    switch (action.type) {
      case actions.deleteItem.TYPE:
        return {...state, [itemId]: {...channel, isDeleting: true}};
      case actions.deleteItemSuccess.TYPE:
        return omit(state, [itemId]);
      case actions.deleteItemError.TYPE:
        return {...state, [itemId]: {...channel, isDeleting: false, deleteItemError: action.payload.err}};

      default:
        return state;
    }
  }

  /**
   * @param items
   * @param {FilterData} filterData
   * @return {*}
   */
  function updateVisibility(items, filterData) {
    if (!options.useFiltering) {
      return items;
    }

    const keys = Object.keys(items);
    let hasChange = false;

    if (keys.length === 0) {
      return items;
    } else {
      const results = keys.reduce((acc, key) => {
        const item = items[key];
        const isVisible = options.isVisible(item, filterData);

        if (item.isVisible === isVisible) {
          acc[key] = item;
        } else {
          hasChange = true;
          acc[key] = Object.assign({}, item, {isVisible});
        }

        return acc;
      }, {});

      return hasChange ? results : items;
    }
  }

  /**
   * @param {ReduxObservableListState} state
   * @param {ReduxAction<UpdateSyncStatusPayload>} action
   * @return {ReduxObservableListState}
   */
  function updateSyncStatusReducer(state, action) {
    if (!hasValidParentId(state, action))
      return state;

    /** @type {UpdateSyncStatusPayload} */
    const payload = action.payload;
    const itemId = getListId(action.payload.id, action.payload.requestId, state.list);
    const existingItem = state.list[itemId];
    if (payload.syncStatus === SyncStatus.UNRECOVERABLE) {
      return existingItem ? {
        ...state,
        list: omit(state.list, [itemId]),
        updates: updatesReducer(state.updates, itemId, payload.syncStatus),
      } : state;

    } else if (existingItem) {
      const currentUpdate = state.updates[itemId];
      const currentStatus = currentUpdate ? currentUpdate.status : SyncStatus.NONE;
      let newStatus = payload.syncStatus;
      if (payload.syncStatus === SyncStatus.SUCCESS && (
        currentStatus === SyncStatus.PENDING
        || currentStatus === SyncStatus.IN_PROGRESS_W_PENDING)
      ) {
        newStatus = SyncStatus.PENDING;
      }

      if (payload.syncStatus === SyncStatus.SUCCESS) {
        const nextState = {
          ...state,
        };

        const updatesResult = updateSyncStatusUpdatesReducer(state, payload, itemId);
        nextState.updates = updatesResult.updates;

        if (payload.model) {
          const newItem = isFunction(options.syncModel) ? options.syncModel(existingItem, payload) : payload.model;
          newItem.syncStatus = updatesResult.newStatus;

          if (payload.model.id !== itemId) {
            nextState.list = omit(state.list, [itemId]);
            nextState.list[payload.model.id] = newItem
          } else {
            nextState.list = {
              ...state.list,
              [itemId]: newItem,
            };
          }
        } else {
          nextState.list = omit(state.list, [itemId]);
        }

        return nextState;
      } else {
        return {
          ...state,
          list: {
            ...state.list,
            [itemId]: {
              ...existingItem,
              syncStatus: newStatus,
            },
          },
          updates: updatesReducer(state.updates, itemId, newStatus),
        };

      }
    } else {
      // TODO: handle item already exists if needed
      console.warn(`updateTimerSyncStatusReducer missing item`);
      return state;
    }
  }

  /**
   * @param {ReduxObservableListState} state
   * @param {UpdateSyncStatusPayload} payload
   * @param {String} itemId
   * @return {ReduxObservableListState~updates}
   */
  function updateSyncStatusUpdatesReducer(state, payload, itemId) {
    const currentUpdate = state.updates[itemId];
    const currentStatus = currentUpdate ? currentUpdate.status : SyncStatus.NONE;
    let newStatus = payload.syncStatus;
    let updates;

    if (payload.syncStatus === SyncStatus.SUCCESS && (
      currentStatus === SyncStatus.PENDING
      || currentStatus === SyncStatus.IN_PROGRESS_W_PENDING)
    ) {
      newStatus = SyncStatus.PENDING;
    }

    if (newStatus === SyncStatus.PENDING) {
      // A newer update request has come in, keep it
      if (payload.model && payload.model.id !== itemId) {
        updates = omit(state.updates, [itemId]);
        updates[payload.model.id] = {
          ...state.updates[itemId],
          status: newStatus,
        };
      } else {
        updates = {
          ...state.updates,
          [itemId]: {
            ...state.updates[itemId],
            status: newStatus,
          },
        };
      }
    } else {
      updates = omit(state.updates, [itemId]);
    }

    return {
      newStatus,
      updates,
    };
  }

  // make update reducer

  function updatesReducer(updates, itemId, status) {
    if (status === SyncStatus.NONE || status === SyncStatus.UNRECOVERABLE) {
      return omit(updates, [itemId])
    } else {
      return {
        ...updates,
        [itemId]: {
          ...updates[itemId],
          status: status,
        },
      }
    }
  }

  /**
   * @param {String} id
   * @param {?String} [requestId]
   * @param [list]
   */
  function getListIdNotCanCreate(id, requestId, list) {
    return id;
  }

  /**
   * @param {?String} id
   * @param {?String} requestId
   * @param list
   */
  function getListIdCanCreate(id, requestId, list) {
    if (id && has(list, id)) {
      return id;
    } else if (requestId && has(list, requestId)) {
      return requestId;
    } else if (requestId) {
      const item = find(list, (value, key) => value.requestId === requestId);
      if (item && has(list, item.id)) {
        return item.id;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/reducers.js